import * as React from 'react'
// libs
import Tooltip from '@mui/material/Tooltip'
// hooks
import { useShowModal } from 'hooks/useShowModal'
import { useCurrentForm } from 'hooks/risks'
// components
import FilterButton from './components/FilterButton'
import VisibilityButton from './components/VisibilityButton'
// views
import {
  Button,
  EditIcon,
  TrashIcon,
  UnarchiveIcon,
  ButtonsWrapper
} from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property/view'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// types
import { Modals } from 'constants/modals'
import { FieldType, IField } from 'types/graphql'
// themes
import { colors } from 'themes/index'
// utils
import { isLinked } from 'utils/form'

interface Props {
  field: IField
  canEdit?: boolean
  archived?: boolean
  canDelete?: boolean
  onUnArchive?: () => void
  canUnarchive?: boolean
  deleteTitle?: string
  onDeleteClick: () => void
}

const ControlButtons: React.FC<Props> = ({
  field,
  canEdit = true,
  archived,
  canDelete = true,
  onUnArchive,
  canUnarchive,
  deleteTitle = 'Delete',
  onDeleteClick
}) => {
  const { linked } = useCurrentForm()

  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const fieldLink = riskTemplate?.fieldLinks?.find(
    ({ field: { id }, parentFieldId }) => id === field.id && parentFieldId === field.parentFieldId
  )

  const { showModal: showFieldDetails } = useShowModal({ modalType: Modals.PROPERTY_DETAILS_MODAL })
  const { showModal: showFormDetails } = useShowModal({ modalType: Modals.RISK_TEMPLATE_ADD_FORM_MODAL })

  const showDetailsDrawer = () => {
    if (fieldLink) {
      showFormDetails({
        field,
        linked: isLinked(fieldLink?.linkingSettings),
        parentId: field.parentFieldId
      })
    } else {
      showFieldDetails({ property: field })
    }
  }

  const canFilter = field.fieldType === FieldType.Lookup

  if (!canDelete && !canEdit && !linked) {
    return null
  }

  return (
    <ButtonsWrapper>
      {canEdit && (
        <Tooltip title='Edit' placement='bottom'>
          <Button onClick={showDetailsDrawer}>
            <EditIcon />
          </Button>
        </Tooltip>
      )}
      {!fieldLink && (
        <>
          {canFilter && <FilterButton field={field} />}
          <VisibilityButton field={field} />
        </>
      )}
      {canUnarchive && (
        <Tooltip title='Unarchive' placement='bottom'>
          <Button onClick={onUnArchive}>
            <UnarchiveIcon />
          </Button>
        </Tooltip>
      )}
      {canDelete && (
        <Tooltip title={deleteTitle} placement='bottom'>
          <Button data-testid='field-delete' onClick={onDeleteClick}>
            <TrashIcon $color={archived ? colors.red300 : colors.blue500} />
          </Button>
        </Tooltip>
      )}
    </ButtonsWrapper>
  )
}
export default ControlButtons
